import { z } from 'zod';
import { BUSINESS_KINDS, LEAD_SEGMENTS, NEXT_ACTIONS, OPPORTUNITY_STAGES, OWNERSHIP_TYPES, PIZZA_FOCUS } from '../constants';

const csvOrArray = <T extends z.ZodTypeAny>(item: T) =>
  z.preprocess((v) => (typeof v === 'string' ? (v === '' ? [] : v.split(',')) : v), z.array(item));

const boolish = z.preprocess((v) => (v === 'true' ? true : v === 'false' ? false : v), z.boolean());

export const MISSING_FIELDS = ['email', 'phone', 'contact', 'decision_maker', 'address', 'website'] as const;
export type MissingField = (typeof MISSING_FIELDS)[number];

export const FOLLOW_UP_FILTERS = ['overdue', 'today', 'week', 'none'] as const;

/**
 * The single lead filter used by the leads table, saved views, CSV export, bulk actions and bulk email.
 * Relative-date fields (…Days) keep saved views fresh. Accepts query-string encoding (csv arrays, 'true').
 */
export const LeadFilter = z.object({
  q: z.string().trim().max(200).optional(),
  /**
   * Pizzerias, dealers and/or reps. The Leads, Dealers and Reps pages each send their own kind; omitted = every
   * kind (dashboard, search and anything that is not one of those lists).
   */
  kinds: csvOrArray(z.enum(BUSINESS_KINDS)).optional(),
  /** Leads / Prospects / Clients / Test Kitchens: each pizzeria list sends its own; omitted = all. */
  segments: csvOrArray(z.enum(LEAD_SEGMENTS)).optional(),
  /** 'me' | 'unassigned' | user uuid, comma separated. */
  owner: csvOrArray(z.union([z.literal('me'), z.literal('unassigned'), z.uuid()])).optional(),
  stageKeys: csvOrArray(z.string().max(60)).optional(),
  relationshipKeys: csvOrArray(z.string().max(60)).optional(),
  tierKeys: csvOrArray(z.string().max(60)).optional(),
  minScore: z.coerce.number().int().min(0).max(100).optional(),
  maxScore: z.coerce.number().int().min(0).max(100).optional(),
  city: z.string().trim().max(120).optional(),
  region: z.string().trim().max(120).optional(),
  country: z.string().trim().max(2).optional(),
  territoryId: z.uuid().optional(),
  sourceIds: csvOrArray(z.uuid()).optional(),
  tagIds: csvOrArray(z.uuid()).optional(),
  ownershipTypes: csvOrArray(z.enum(OWNERSHIP_TYPES)).optional(),
  pizzaFocus: csvOrArray(z.enum(PIZZA_FOCUS)).optional(),
  hasOpenOpportunity: boolish.optional(),
  opportunityStages: csvOrArray(z.enum(OPPORTUNITY_STAGES)).optional(),
  lastContactOlderThanDays: z.coerce.number().int().min(1).max(3650).optional(),
  neverContacted: boolish.optional(),
  followUp: z.enum(FOLLOW_UP_FILTERS).optional(),
  createdWithinDays: z.coerce.number().int().min(1).max(3650).optional(),
  missing: csvOrArray(z.enum(MISSING_FIELDS)).optional(),
  nextActions: csvOrArray(z.enum(NEXT_ACTIONS)).optional(),
  doNotContact: boolish.optional(),
  hasPendingDuplicate: boolish.optional(),
  /** Leads an import created or merged rows into (rows skipped, junk or failed are not its leads). */
  importJobId: z.uuid().optional(),
  /** Same, for every import of one folder scan (import_jobs.batch_id). */
  importBatchId: z.uuid().optional(),
  /** Managers/admins only: show soft-deleted leads (for restore). */
  deleted: boolish.optional(),
});
export type LeadFilter = z.infer<typeof LeadFilter>;

export const LEAD_SORT_FIELDS = [
  'score',
  'name',
  'city',
  'lastContactedAt',
  'nextFollowUpAt',
  'estimatedValue',
  'createdAt',
  'updatedAt',
  'stage',
  'relationship',
] as const;
export type LeadSortField = (typeof LEAD_SORT_FIELDS)[number];

export const LeadListQuery = LeadFilter.extend({
  sort: z.enum(LEAD_SORT_FIELDS).default('score'),
  dir: z.enum(['asc', 'desc']).default('desc'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type LeadListQuery = z.infer<typeof LeadListQuery>;

/** Selection for bulk operations: explicit ids, or "all results matching this filter" minus exclusions. */
export const LeadSelection = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('ids'), ids: z.array(z.uuid()).min(1).max(5000) }),
  z.object({
    mode: z.literal('filter'),
    filter: LeadFilter,
    excludeIds: z.array(z.uuid()).max(5000).default([]),
  }),
]);
export type LeadSelection = z.infer<typeof LeadSelection>;

/** Columns available in the lead table (column customization + CSV export). */
export const LEAD_COLUMNS = [
  'business',
  'kind',
  'city',
  'score',
  'tier',
  'relationship',
  'stage',
  'contact',
  'phone',
  'email',
  'lastContactedAt',
  'nextFollowUpAt',
  'nextAction',
  'estimatedValue',
  'owner',
  'source',
  'tags',
  'createdAt',
] as const;
export type LeadColumn = (typeof LEAD_COLUMNS)[number];

export const DEFAULT_LEAD_COLUMNS: LeadColumn[] = [
  'business',
  'city',
  'score',
  'tier',
  'relationship',
  'stage',
  'contact',
  'lastContactedAt',
  'nextFollowUpAt',
  'estimatedValue',
  'owner',
  'source',
];
